"use client";

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { toast } from "sonner";
import { userAdminQueryKeys } from "./user-admin-query-keys";
import {
  getAdminUser,
  getAdminUsers,
  getAdminUserStats,
  setUserRoles,
  type ListUsersParams,
  type PagedUsersResponse,
  type FilterParams,
  type UserAdminStatsDto,
  type UserAdminDetailDto,
  type CustomClaim,
} from "@/app/admin/users/_api/user-admin-reads";

export function useAdminUser(
  id: string,
  options?: Omit<UseQueryOptions<UserAdminDetailDto>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: userAdminQueryKeys.detail(id),
    queryFn: () => getAdminUser(id),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useAdminUsers(
  params: ListUsersParams,
  options?: Omit<UseQueryOptions<PagedUsersResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: userAdminQueryKeys.list(params),
    queryFn: () => getAdminUsers(params),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useSetUserRoles(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roles: CustomClaim[]) => setUserRoles(userId, roles),
    onSuccess: (updated) => {
      queryClient.setQueryData(userAdminQueryKeys.detail(userId), updated);
    },
    onError: () => {
      toast.error("Rollen konnten nicht gespeichert werden.");
    },
  });
}

export function useAdminUserStats(
  params: FilterParams,
  options?: Omit<UseQueryOptions<UserAdminStatsDto>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: userAdminQueryKeys.stats(params),
    queryFn: () => getAdminUserStats(params),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}
